import { create } from 'zustand';
import { supabase } from './supabase';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { log } from '../components/DebugLogger';

export type CardFinish = 'normal' | 'holofoil' | 'reverse_holofoil' | 'poke_ball' | 'master_ball';

export interface CollectionCard {
  id: string;
  cardId: string;
  setId: string;
  quantity: number;
  finish: CardFinish;
  username?: string;
  card_name?: string;
  card_number?: string;
  set_name?: string;
}

interface CollectionStore {
  collection: CollectionCard[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  isLoading: boolean;
  // Actions
  addCard: (card: Card, quantity?: number, finish?: CardFinish) => Promise<void>;
  removeCard: (cardId: string, finish: CardFinish, removeAll?: boolean) => Promise<void>;
  updateCard: (cardId: string, updates: Partial<CollectionCard>) => Promise<void>;
  loadCollection: () => Promise<void>;
  getCardQuantity: (cardId: string, finish: CardFinish) => number;
  getCardDetails: (cardId: string, finish: CardFinish) => CollectionCard | undefined;
  reset: () => void;
}

export const useCollectionStore = create<CollectionStore>((set, get) => ({
  collection: [],
  loading: false,
  error: null,
  initialized: false,
  isLoading: false,

  reset: () => {
    set({
      collection: [],
      loading: false,
      error: null,
      initialized: false,
      isLoading: false
    });
  },

  addCard: async (card, quantity = 1, finish = 'normal') => {
    try {
      set({ loading: true, error: null });
      log(`Starting addCard operation for ${card.name}...`);
      const currentState = get();

      if (!card || !card.id || !card.set) {
        log('Invalid card data provided to addCard');
        console.error('Invalid card data:', card);
        throw new Error('Invalid card data: missing required fields');
      }

      const setId = card.set.id;
      if (!setId) {
        console.error('Invalid card data:', card);
        throw new Error('Invalid card data: missing set identifier');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        log('No active session found');
        throw new Error('You must be signed in to add cards to your collection');
      }
      
      log(`Checking for existing card in collection: ${card.name}`);
      const { data: existingCards, error: fetchError } = await supabase
        .from('collections')
        .select()
        .eq('card_id', card.id)
        .eq('user_id', session.user.id)
        .eq('card_finish', finish);

      if (fetchError) throw fetchError;

      const existingCard = existingCards?.[0];

      if (existingCard) {
        log(`Updating existing card quantity from ${existingCard.quantity} to ${existingCard.quantity + quantity}`);
        const newQuantity = existingCard.quantity + quantity;
        const { error } = await supabase
          .from('collections')
          .update({ 
            quantity: newQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('card_id', card.id)
          .eq('user_id', session.user.id)
          .eq('card_finish', finish);

        if (error) throw error;

        // Update local state immediately
        set({
          collection: currentState.collection.map(c => 
            c.cardId === card.id && c.finish === finish
              ? { ...c, quantity: newQuantity }
              : c
          )
        });
        log(`Local state updated for ${card.name}`);

        // Trigger collection reload
        await get().loadCollection();
      } else {
        const newCard = {
          id: crypto.randomUUID(),
          cardId: card.id,
          setId: setId,
          quantity,
          finish
        };

        log('Adding new card to collection');
        const { error } = await supabase
          .from('collections')
          .insert({
            card_id: newCard.cardId,
            user_id: session.user.id,
            set_id: newCard.setId,
            quantity,
            card_finish: finish,
            card_name: card.name,
            card_number: card.number,
            set_name: card.set.name
          });

        if (error) {
          console.error('Error adding card:', error);
          log('Error adding card to collection:', error.message);
          throw new Error(error.message);
        }

        log('Successfully added new card to collection');
        
        // Update local state immediately
        set({
          collection: [...currentState.collection, newCard]
        });
        log(`Local state updated with new card ${card.name}`);

        // Trigger collection reload
        await get().loadCollection();
      }
    } catch (error) {
      log('Error in addCard operation:', error instanceof Error ? error.message : 'Unknown error');
      console.error('Error in addCard:', error);
      set({ error: error instanceof Error ? error.message : 'Error adding card' });
      throw error;
    } finally {
      set({ loading: false });
      log('Completed addCard operation');
    }
  },

  removeCard: async (cardId, finish, removeAll = false) => {
    try {
      set({ loading: true, error: null });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be signed in to remove cards from your collection');
      }

      const existingCard = get().collection.find(c => c.cardId === cardId && c.finish === finish);
      if (!existingCard) {
        throw new Error('Card not found in collection');
      }

      if (removeAll || existingCard.quantity <= 1) {
        // Remove the entire card entry
        const { error } = await supabase
          .from('collections')
          .delete()
          .eq('card_id', cardId)
          .eq('user_id', session.user.id)
          .eq('card_finish', finish);

        if (error) throw error;

        // Update local state immediately
        set(state => ({
          collection: state.collection.filter(card => 
            !(card.cardId === cardId && card.finish === finish)
          )
        }));

        // Trigger collection reload
        await get().loadCollection();
      } else {
        // Decrease quantity by 1
        const newQuantity = existingCard.quantity - 1;
        const { error } = await supabase
          .from('collections')
          .update({ 
            quantity: newQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('card_id', cardId)
          .eq('user_id', session.user.id)
          .eq('card_finish', finish);

        if (error) throw error;

        // Update local state immediately
        set(state => ({
          collection: state.collection.map(card => 
            card.cardId === cardId && card.finish === finish
              ? { ...card, quantity: newQuantity }
              : card
          )
        }));

        // Trigger collection reload
        await get().loadCollection();
      }
    } catch (error) {
      console.error('Error removing card:', error);
      set({ error: error instanceof Error ? error.message : 'Error removing card' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateCard: async (cardId, updates) => {
    try {
      set({ loading: true, error: null });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be signed in to update your collection');
      }

      if (updates.quantity !== undefined && updates.quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
      }
      
      const { error } = await supabase
        .from('collections')
        .update({
          ...updates,
          card_finish: updates.finish,
          updated_at: new Date().toISOString()
        })
        .eq('card_id', cardId)
        .eq('user_id', session.user.id)
        .eq('card_finish', updates.finish);

      if (error) throw error;

      // Update local state immediately
      set(state => ({
        collection: state.collection.map(card => 
          card.cardId === cardId && card.finish === updates.finish
            ? { ...card, ...updates }
            : card
        )
      }));

      // Trigger collection reload
      await get().loadCollection();
    } catch (error) {
      console.error('Error updating card:', error);
      set({ error: error instanceof Error ? error.message : 'Error updating card' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  loadCollection: async () => {
    try {
      if (get().isLoading) {
        return;
      }

      set({ loading: true, error: null, isLoading: true });
      log('Starting collection load');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        log('No active session, resetting collection');
        set({ collection: [], initialized: true, loading: false, isLoading: false });
        return;
      }
      
      log('Fetching collection from Supabase');
      const { data, error } = await supabase
        .from('collections')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) {
        log(`Error fetching collection: ${error.message}`);
        throw error;
      }

      if (!data) {
        log('No collection data returned');
        set({ collection: [], initialized: true, loading: false, isLoading: false });
        return;
      }

      log(`Loaded ${data.length} cards from collection`);
      set({ 
        collection: data.map(item => ({
          id: item.id,
          cardId: item.card_id,
          setId: item.set_id,
          quantity: item.quantity,
          finish: item.card_finish || 'normal',
          username: item.username,
          card_name: item.card_name,
          card_number: item.card_number,
          set_name: item.set_name
        })),
        initialized: true
      });
      log('Collection state updated successfully');
    } catch (error) {
      log(`Error loading collection: ${error instanceof Error ? error.message : 'Unknown error'}`);
      set({ error: error instanceof Error ? error.message : 'Error loading collection' });
    } finally {
      set({ loading: false, isLoading: false });
      log('Collection load complete');
    }
  },

  getCardQuantity: (cardId, finish) => {
    const card = get().collection.find(c => c.cardId === cardId && c.finish === finish);
    return card?.quantity || 0;
  },

  getCardDetails: (cardId, finish) => {
    return get().collection.find(c => c.cardId === cardId && c.finish === finish);
  }
}));